import { Box, Typography, useTheme } from '@mui/joy';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { Link } from 'core/elements';
import { Color, CustomColor, Level, Variant } from 'theme/enums';

export type MenuItem = {
  label: string;
  icon?: ReactNode;
  // `to` for in-app routes, rendered through the router so the hop does not
  // reload the page; `href` stays for V1 and external URLs. An absolute URL in
  // `href` can never equal `pathname`, so highlighting and the submenu auto-open
  // only work for entries that use `to`.
  to?: string;
  href?: string;
  hasCaret?: boolean;
  hide?: boolean;
  items?: MenuItem[];
};

const getItemPath = (item: MenuItem) => item.to ?? item.href;

const holdsPath = (item: MenuItem, pathname: string): boolean =>
  Boolean(
    item.items?.some((child) => getItemPath(child) === pathname || holdsPath(child, pathname))
  );

export const MenuList = ({
  items,
  parentItem,
  onBack,
}: {
  items: MenuItem[];
  parentItem?: MenuItem;
  onBack?: () => void;
}) => {
  const [openedChild, setOpenedChild] = useState<MenuItem | null>(null);

  const theme = useTheme();

  const { pathname } = useLocation();

  const { t } = useTranslation();

  // Each nesting level is rendered by its own MenuList instance, so filtering here also covers
  // sub-menu entries.
  const visibleItems = useMemo(() => items.filter(({ hide }) => !hide), [items]);

  useEffect(() => {
    const currentChild = visibleItems.find((item) => holdsPath(item, pathname));

    setOpenedChild(currentChild || null);
  }, [pathname, visibleItems]);

  if (openedChild) {
    return (
      <MenuList
        items={openedChild.items}
        parentItem={openedChild}
        onBack={() => setOpenedChild(null)}
      />
    );
  }

  return (
    <Box sx={{ width: '200px', paddingTop: 1 }}>
      {onBack && (
        <Link onClick={onBack} variant={Variant.PLAIN} color={Color.NEUTRAL}>
          <CaretLeft />
          {t('labelBackToMainMenu')}
        </Link>
      )}
      {parentItem && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 2,
            marginTop: 2,
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              backgroundColor: 'primary.500',
              padding: 0.5,
              borderRadius: 4,
              marginRight: 1,
              color: theme.palette.common[CustomColor.WHITE],
            }}
          >
            {parentItem.icon}
          </Box>
          <Typography level={Level.TITLE_MD}>{parentItem.label}</Typography>
        </Box>
      )}
      {visibleItems.map((item, index) => (
        <Box key={index}>
          <Link
            {...(item.to ? { component: RouterLink, to: item.to } : { href: item.href })}
            // A group is active while the open page sits under it, so "Back to Main Menu" still
            // shows where the user is.
            highlight={pathname === getItemPath(item) || holdsPath(item, pathname)}
            onClick={() => {
              if (item.items) {
                setOpenedChild(item);
              }
            }}
            variant={Variant.PLAIN}
            color={Color.NEUTRAL}
            sx={{ width: '100%', marginBottom: 1, padding: 1 }}
          >
            {item.icon && <Box sx={{ marginRight: 1 }}>{item.icon}</Box>}
            {item.label}
            {item.hasCaret && (
              <Box sx={{ marginLeft: 'auto' }}>
                <CaretRight />
              </Box>
            )}
          </Link>
        </Box>
      ))}
    </Box>
  );
};
